import {FastArrowLeft, NavArrowDown, NavArrowRight} from "iconoir-react";
import React, {
    Children,
    cloneElement,
    createContext,
    ForwardRefExoticComponent,
    ReactElement,
    SVGProps,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import Collapse from "react-bootstrap/Collapse";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Overlay from "react-bootstrap/Overlay";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Tooltip from "react-bootstrap/Tooltip";
import {NavLink, useLocation} from "react-router-dom";
import "./SideBar.scss";

// ---------------------------------------------------------------------------
// SidebarCollapseContext — shared collapse state, accessible outside SideBar
// ---------------------------------------------------------------------------

interface SidebarCollapseContextType {
    collapsed: boolean;
    setCollapsed: (next: boolean) => void;
    isProvided: boolean;
}

const SidebarCollapseContext = createContext<SidebarCollapseContextType>({
    collapsed: false,
    setCollapsed: () => {},
    isProvided: false,
});

// Auto-collapse when below Bootstrap xl (1200 px).
// The desktop sidebar is only shown at lg+ (≥992 px), so this covers the
// 992–1199 px range where space is tight.
const COLLAPSE_MQ = "(max-width: 1199px)";

interface SidebarCollapseProviderProps {
    children: React.ReactNode;
}

/**
 * Provides sidebar collapse state to any component in its subtree.
 * Place this above both the SideBar and the main content if you need
 * useSidebarCollapsed() outside the SideBar component itself.
 */
export const SidebarCollapseProvider = ({
    children,
}: SidebarCollapseProviderProps) => {
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia(COLLAPSE_MQ);
        const handleChange = (e: MediaQueryList | MediaQueryListEvent) =>
            setCollapsed(e.matches);

        handleChange(mq);
        mq.addEventListener(
            "change",
            handleChange as (e: MediaQueryListEvent) => void,
        );
        return () =>
            mq.removeEventListener(
                "change",
                handleChange as (e: MediaQueryListEvent) => void,
            );
    }, []);

    return (
        <SidebarCollapseContext.Provider
            value={{ collapsed, setCollapsed, isProvided: true }}>
            {children}
        </SidebarCollapseContext.Provider>
    );
};

// ---------------------------------------------------------------------------
// SidebarContext — internal context used within the SideBar tree
// ---------------------------------------------------------------------------

interface SidebarContextType {
    collapsed: boolean;
    insidePopover: boolean;
    depth: number;
    onItemSelected?: () => void;
}

const SidebarContext = createContext<SidebarContextType>({
    collapsed: false,
    insidePopover: false,
    depth: 0,
});

interface SideBarDropdownProps {
    title: string;
    forceClose?: boolean;
    children: React.ReactNode;
    icon?: ReactElement<SVGProps<never>>;
}

/**
 * Checks if the given ReactNode contains any `SideBarItem` components, either directly or nested within its children.
 */
function containsSideBarItems(children: React.ReactNode): boolean {
    let result = false;

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;

        if (child.type === SideBarItem) {
            result = true;
        } else if (child.props?.children) {
            result = result || containsSideBarItems(child.props.children);
        }
    });

    return result;
}

/**
 * Returns true if the given link matches the current pathname.
 * Absolute links (starting with '/') use prefix matching.
 * Relative links are matched as a suffix against the pathname so they work
 * regardless of the base route (e.g. 'settings/general/foo' matches '/users/settings/general/foo').
 */
function isLinkActive(link: string, pathname: string): boolean {
    if (link.startsWith("/")) {
        return pathname === link || pathname.startsWith(link + "/");
    }
    return pathname.endsWith("/" + link) || pathname.includes("/" + link + "/");
}

/**
 * Checks if any descendant SideBarItem has a link matching the given pathname.
 */
function containsActiveItem(
    children: React.ReactNode,
    pathname: string,
): boolean {
    let result = false;

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;

        if (child.type === SideBarItem) {
            const link = child.props.link as string | undefined;
            if (link !== undefined) {
                result = result || isLinkActive(link, pathname);
            }
        } else if (child.props?.children) {
            result =
                result || containsActiveItem(child.props.children, pathname);
        }
    });

    return result;
}

export const SideBarDropdown = ({
    title,
    children,
    forceClose,
    icon,
}: SideBarDropdownProps) => {
    const { collapsed, insidePopover, depth } = useContext(SidebarContext);
    const [open, setOpen] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const location = useLocation();

    // Compute once per render — reused for both the early-return guard and the effect dep.
    // When children are built asynchronously (e.g. SettingsMenuBuilder waiting for API data),
    // hasItems flips false→true after loading, which re-triggers the auto-open effect.
    const hasItems = containsSideBarItems(children);

    useEffect(() => {
        if (forceClose) setOpen(false);
    }, [forceClose]);

    // Auto-open when a child item matches the current URL (e.g. on direct navigation).
    // hasItems is included so the effect also fires when async children finish loading.
    useEffect(() => {
        if (hasItems && containsActiveItem(children, location.pathname)) {
            setOpen(true);
        }
    }, [location.pathname, hasItems]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close flyout popover when the user navigates to a new route
    useEffect(() => {
        setShowPopover(false);
    }, [location]);

    useEffect(
        () => () => {
            if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        },
        [],
    );

    const handleMouseEnter = useCallback(() => {
        if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        setShowPopover(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        leaveTimerRef.current = setTimeout(() => setShowPopover(false), 200);
    }, []);

    // On touch devices a tap fires mouseenter→mouseleave in quick succession,
    // so the 200 ms close timer starts before the popover is useful. Handling
    // onClick cancels that timer and keeps the popover open after a tap.
    const handleTriggerClick = useCallback(() => {
        if (leaveTimerRef.current) {
            clearTimeout(leaveTimerRef.current);
            leaveTimerRef.current = null;
        }
        setShowPopover(true);
    }, []);

    if (!hasItems) return null;

    // Collapsed top-level: show icon-only trigger with a flyout popover on hover
    if (collapsed && !insidePopover && depth === 0) {
        const iconEl = icon ?? (
            <span className="sidebar-icon-fallback">{title[0]}</span>
        );

        return (
            <div
                ref={triggerRef}
                className="sidebar-dropdown-icon-trigger"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleTriggerClick}>
                <div className="sidebar-icon-only-btn">{iconEl}</div>
                <Overlay
                    show={showPopover}
                    target={triggerRef}
                    placement="right-start"
                    rootClose
                    onHide={() => setShowPopover(false)}>
                    {/*
                     * onClick stopPropagation is required here.
                     *
                     * React bubbles synthetic events through the React fiber tree, NOT the DOM tree.
                     * Even though this Popover is rendered into document.body via a Portal, clicks
                     * inside it bubble up through the fiber tree and would reach the trigger div above,
                     * firing handleTriggerClick → setShowPopover(true). Without stopPropagation, any
                     * click inside the flyout that also calls setShowPopover(false) (e.g. via
                     * onItemSelected below) would be immediately undone by that re-open.
                     *
                     * NavLink items close the flyout via the location-change useEffect instead
                     * of onItemSelected, so they are also unaffected by stopping propagation here.
                     */}
                    <Popover
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onClick={(e) => e.stopPropagation()}
                        className="sidebar-flyout-popover">
                        <Popover.Body className="px-2 py-2">
                            {/*
                             * onItemSelected is passed via context to SideBarItem children.
                             * When a custom component (component variant of SideBarItem) is clicked,
                             * SideBarItem wraps it in a div with onClick={onItemSelected} so the
                             * flyout closes. NavLink items don't need this — navigation triggers
                             * the location-change useEffect which closes the flyout.
                             */}
                            <SidebarContext.Provider
                                value={{
                                    collapsed: false,
                                    insidePopover: true,
                                    depth: 0,
                                    onItemSelected: () => setShowPopover(false),
                                }}>
                                <Nav className="flex-column">{children}</Nav>
                            </SidebarContext.Provider>
                        </Popover.Body>
                    </Popover>
                </Overlay>
            </div>
        );
    }

    // Normal (expanded) mode: standard collapsible dropdown
    return (
        <SidebarContext.Provider
            value={{ collapsed, insidePopover, depth: depth + 1 }}>
            <Container fluid className="sidebar-dropdown">
                <Nav.Item
                    onClick={() => setOpen(!open)}
                    className="nav-link sidebar-dropdown-trigger user-select-none d-flex align-items-center">
                    <span className="sidebar-dropdown-title w-100 d-flex align-items-center">
                        {icon}
                        <span className="me-2" />
                        {title}
                    </span>
                    {open ? (
                        <NavArrowDown
                            strokeWidth={2}
                            className="sidebar-dropdown-chevron"
                        />
                    ) : (
                        <NavArrowRight
                            strokeWidth={2}
                            className="sidebar-dropdown-chevron"
                        />
                    )}
                </Nav.Item>
                <Collapse in={open}>
                    <div>
                        <div className="pb-3">
                            {Children.map(
                                children as React.ReactElement,
                                (child) =>
                                    child !== null &&
                                    cloneElement(child, { forceClose: !open }),
                            )}
                        </div>
                    </div>
                </Collapse>
            </Container>
        </SidebarContext.Provider>
    );
};

type SideBarItemProps =
    | {
          title: string;
          link: string;
          badge?: number | string;
          icon?: ReactElement<
              ForwardRefExoticComponent<
                  Omit<SVGProps<SVGSVGElement>, string | number | symbol>
              >
          >;
          component?: never;
      }
    | {
          title?: string;
          link?: never;
          icon?: never;
          badge?: never;
          component: React.ReactNode;
      };

export const SideBarItem = ({
    title,
    link,
    icon,
    badge,
    component,
}: SideBarItemProps) => {
    const { collapsed, insidePopover, depth, onItemSelected } =
        useContext(SidebarContext);

    // Custom component: render directly; hidden in collapsed icon-only mode
    if (component !== undefined) {
        if (collapsed && !insidePopover && depth === 0) {
            return null;
        }
        // When inside a flyout (onItemSelected is set), wrap in a div that closes the flyout on
        // any click. This handles custom components (e.g. buttons) that don't navigate and
        // therefore can't rely on the location-change useEffect in SideBarDropdown to close it.
        if (onItemSelected) {
            return <div onClick={onItemSelected}>{component}</div>;
        }
        return <>{component}</>;
    }

    // Collapsed top-level: show icon-only with a tooltip
    if (collapsed && !insidePopover && depth === 0) {
        const iconEl = icon ?? (
            <span className="sidebar-icon-fallback">{title![0]}</span>
        );

        return (
            <OverlayTrigger
                placement="right"
                overlay={<Tooltip>{title}</Tooltip>}>
                <Container fluid>
                    <Nav.Item className="sidebar-item mt-1 mb-1">
                        <NavLink
                            to={link!}
                            className="nav-link sidebar-icon-only-btn">
                            {iconEl}
                        </NavLink>
                    </Nav.Item>
                </Container>
            </OverlayTrigger>
        );
    }

    return (
        <Container fluid>
            <Nav.Item className="nav-pills user-select-none sidebar-item mt-1 mb-1">
                <NavLink to={link!} className="nav-link">
                    <div className="d-flex align-items-center sidebar-item-title">
                        {icon}
                        <span className="me-2" />
                        {title}
                    </div>
                    {badge !== undefined && (
                        <span className="sidebar-item-badge">{badge}</span>
                    )}
                </NavLink>
            </Nav.Item>
        </Container>
    );
};

interface SideBarProps {
    children: React.ReactNode;
    className?: string;
}

/**
 * A standalone collapse/expand toggle button that can be placed anywhere inside
 * a SidebarCollapseProvider tree — e.g. in the navbar-brand area of AppLayout.
 * Hidden on small screens (below Bootstrap xl breakpoint) via CSS.
 */
export const SidebarToggleButton = () => {
    const { collapsed, setCollapsed } = useContext(SidebarCollapseContext);
    const handleToggle = useCallback(
        () => setCollapsed(!collapsed),
        [setCollapsed, collapsed],
    );

    return (
        <button
            className="sidebar-toggle-btn"
            onClick={handleToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <FastArrowLeft
                width="16"
                height="16"
                style={{
                    transform: collapsed ? "scaleX(-1)" : "none",
                    transition: "transform 0.25s ease",
                }}
            />
        </button>
    );
};

const SideBarCore = ({ children, className }: SideBarProps) => {
    const { collapsed } = useContext(SidebarCollapseContext);
    const navRef = useRef<HTMLDivElement>(null);

    // Sync the .sidebar-collapsed CSS class on the nearest .sidebar-column ancestor
    useEffect(() => {
        const sidebarEl =
            navRef.current?.closest<HTMLElement>(".sidebar-column");
        if (sidebarEl) {
            sidebarEl.classList.toggle("sidebar-collapsed", collapsed);
        }
    }, [collapsed]);

    return (
        <SidebarContext.Provider
            value={{ collapsed, insidePopover: false, depth: 0 }}>
            <div ref={navRef} className="h-100">
                <Nav className={`flex-column sidebar-nav ${className ?? ""}`}>
                    {children}
                </Nav>
            </div>
        </SidebarContext.Provider>
    );
};

export const SideBar = ({ children, className }: SideBarProps) => {
    const { isProvided } = useContext(SidebarCollapseContext);

    if (!isProvided) {
        return (
            <SidebarCollapseProvider>
                <SideBarCore className={className}>{children}</SideBarCore>
            </SidebarCollapseProvider>
        );
    }

    return <SideBarCore className={className}>{children}</SideBarCore>;
};

SideBar.Item = SideBarItem;
SideBar.Dropdown = SideBarDropdown;

/**
 * Returns true when the nearest SidebarCollapseProvider ancestor has the sidebar collapsed.
 * Works both inside and outside the SideBar component tree, as long as a
 * SidebarCollapseProvider is present above in the tree.
 */
export function useSidebarCollapsed(): boolean {
    return useContext(SidebarCollapseContext).collapsed;
}
